"use client";

import { useState } from "react";
import { countryName } from "@/lib/conditions/country-names";
import type { CountryHolidays, FeederWeight, HolidayBar } from "@/lib/conditions/busyness-data";

type Props = {
  holidays: CountryHolidays[];
  seasonStart: string;
  seasonEnd: string;
  feederWeights?: FeederWeight[];
};

function daysBetween(a: string, b: string): number {
  return (Date.UTC(
    +b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10),
  ) - Date.UTC(
    +a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10),
  )) / 86_400_000;
}

type MonthLabel = { label: string; offsetPct: number; widthPct: number };

function monthLabels(start: string, end: string): MonthLabel[] {
  const totalDays = daysBetween(start, end) + 1;
  const labels: MonthLabel[] = [];
  const SHORT_MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const startDate = new Date(start + "T00:00:00Z");
  let cursor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), 1));

  while (true) {
    const monthStart = cursor.toISOString().slice(0, 10);
    const nextMonth = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
    const monthEndDate = new Date(nextMonth.getTime() - 86_400_000);
    const monthEnd = monthEndDate.toISOString().slice(0, 10);

    const visibleStart = monthStart < start ? start : monthStart;
    const visibleEnd = monthEnd > end ? end : monthEnd;

    if (visibleStart > end) break;

    const offsetDays = daysBetween(start, visibleStart);
    const spanDays = daysBetween(visibleStart, visibleEnd) + 1;

    labels.push({
      label: SHORT_MONTHS[cursor.getUTCMonth()],
      offsetPct: (offsetDays / totalDays) * 100,
      widthPct: (spanDays / totalDays) * 100,
    });

    cursor = nextMonth;
  }

  return labels;
}

function formatDate(iso: string): string {
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

function formatDateRange(start: string, end: string): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

type HolidayCategory = "christmas" | "halfterm" | "easter" | "summer" | "other";

function classifyHoliday(name: string): HolidayCategory {
  const lower = name.toLowerCase();
  if (lower.includes("christmas") || lower.includes("new year")) return "christmas";
  if (lower.includes("half-term") || lower.includes("half term") || lower.includes("february") || lower.includes("mid-winter") || lower.includes("carnival")) return "halfterm";
  if (lower.includes("easter") || lower.includes("spring")) return "easter";
  if (lower.includes("summer")) return "summer";
  if (lower.includes("winter")) return "christmas";
  return "other";
}

const HOLIDAY_COLOURS: Record<HolidayCategory, string> = {
  christmas: "bg-rose-500",
  halfterm: "bg-amber-500",
  easter: "bg-violet-500",
  summer: "bg-emerald-500",
  other: "bg-slate-400",
};

const HOLIDAY_LABELS: Record<HolidayCategory, string> = {
  christmas: "Christmas / New Year",
  halfterm: "Half-term / mid-winter",
  easter: "Easter / spring",
  summer: "Summer",
  other: "Other",
};

export default function GanttTimeline({
  holidays,
  seasonStart,
  seasonEnd,
  feederWeights,
}: Props) {
  const [activeBar, setActiveBar] = useState<HolidayBar | null>(null);
  const totalDays = daysBetween(seasonStart, seasonEnd) + 1;
  const months = monthLabels(seasonStart, seasonEnd);

  const weightMap = new Map<string, number>();
  if (feederWeights) {
    for (const fw of feederWeights) {
      weightMap.set(fw.countryCode, fw.weight);
    }
  }

  const hasWeights = feederWeights && feederWeights.length > 0;

  const visibleCountries = (
    hasWeights
      ? holidays.filter((ch) => weightMap.has(ch.countryCode))
      : holidays
  )
    .slice()
    .sort((a, b) => {
      if (!hasWeights) return a.countryCode.localeCompare(b.countryCode);
      const wA = weightMap.get(a.countryCode) ?? 0;
      const wB = weightMap.get(b.countryCode) ?? 0;
      return wB - wA;
    });

  function barOpacity(code: string): number {
    if (!hasWeights) return 0.6;
    const w = weightMap.get(code) ?? 0;
    return 0.3 + w * 0.7;
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        {/* Month header row */}
        <div className="relative h-6 ml-40 mb-1">
          {months.map((m) => (
            <span
              key={`${m.label}-${m.offsetPct}`}
              className="absolute text-[10px] font-semibold text-text-muted uppercase tracking-wide text-center"
              style={{
                left: `${m.offsetPct}%`,
                width: `${m.widthPct}%`,
              }}
            >
              {m.label}
            </span>
          ))}
        </div>

        {/* Country rows */}
        <div className="space-y-1">
          {visibleCountries.map((country) => {
            const weight = weightMap.get(country.countryCode);

            return (
              <div key={country.countryCode} className="flex items-center">
                {/* Label: full country name */}
                <div className="w-40 shrink-0 text-[11px] font-semibold text-navy text-right pr-3 truncate">
                  {countryName(country.countryCode)}
                  {hasWeights && weight != null && (
                    <span className="text-text-muted font-normal ml-1">
                      {Math.round(weight * 100)}%
                    </span>
                  )}
                </div>

                {/* Bar track */}
                <div className="flex-1 relative h-5 bg-warm-bg/50 rounded">
                  {country.holidays.map((bar, i) => {
                    const startOffset = daysBetween(seasonStart, bar.startDate);
                    const barDays = daysBetween(bar.startDate, bar.endDate) + 1;

                    let leftPct = (startOffset / totalDays) * 100;
                    let widthPct = (barDays / totalDays) * 100;

                    if (leftPct < 0) {
                      widthPct += leftPct;
                      leftPct = 0;
                    }
                    if (leftPct + widthPct > 100) {
                      widthPct = 100 - leftPct;
                    }
                    if (widthPct <= 0) return null;

                    const isActive = activeBar === bar;

                    const category = classifyHoliday(bar.holidayName);

                    return (
                      <button
                        key={`${bar.countryCode}-${bar.holidayName}-${bar.startDate}-${i}`}
                        type="button"
                        aria-label={`${countryName(bar.countryCode)}: ${bar.holidayName}, ${formatDateRange(bar.startDate, bar.endDate)}`}
                        className={`absolute top-0.5 bottom-0.5 rounded-sm p-0 ${HOLIDAY_COLOURS[category]} cursor-pointer transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 ${
                          isActive ? "ring-2 ring-gold/60" : ""
                        }`}
                        style={{
                          left: `${leftPct}%`,
                          width: `${widthPct}%`,
                          opacity: barOpacity(bar.countryCode),
                        }}
                        onMouseEnter={() => setActiveBar(bar)}
                        onMouseLeave={() => setActiveBar(null)}
                        onFocus={() => setActiveBar(bar)}
                        onBlur={() => setActiveBar(null)}
                        onClick={() => setActiveBar(isActive ? null : bar)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-[11px] text-text-muted ml-40">
          {(Object.keys(HOLIDAY_COLOURS) as HolidayCategory[]).map((cat) => (
            <div key={cat} className="flex items-center gap-1.5">
              <span className={`w-5 h-3 rounded-sm ${HOLIDAY_COLOURS[cat]}`} />
              {HOLIDAY_LABELS[cat]}
            </div>
          ))}
        </div>
      </div>

      {/* Popover detail card */}
      {activeBar && (
        <div role="status" aria-live="polite" className="mt-4 bg-white rounded-[12px] border border-border-light p-4 shadow-[0_4px_16px_rgba(0,0,0,0.06)] animate-fade-in">
          <div className="flex items-center gap-3 mb-1">
            <span className="font-serif text-[15px] font-semibold text-navy">
              {countryName(activeBar.countryCode)}
            </span>
            {hasWeights && weightMap.has(activeBar.countryCode) && (
              <span className="text-[11px] text-text-muted">
                {Math.round((weightMap.get(activeBar.countryCode) ?? 0) * 100)}% feeder weight
              </span>
            )}
          </div>
          <p className="text-[13px] text-navy font-medium">{activeBar.holidayName}</p>
          <p className="text-[12px] text-text-muted mt-0.5">
            {formatDateRange(activeBar.startDate, activeBar.endDate)}
          </p>
        </div>
      )}
    </div>
  );
}
